/**
 * KTRADECRAFT — Tradecraft library substrate (KTRADECRAFT-IMPL-1, Council 008 Path A).
 *
 * Per-vertical operational tradecraft modules — domain-specific patterns,
 * regulatory nuances, exception-resolution playbooks.
 *
 * Architecture rules:
 *   - Admin-write; tenant-read filtered by vertical tag.
 *   - LLM-synthesized modules flagged synthesized=true with confidence; active=false until reviewed.
 *   - Operator review required before active=true (quality gate).
 *   - Injection into AEGIS prompts via governedAegisCall tradecraftContext flag.
 *   - Append-versioned: no in-place updates; new version row per change.
 */

import DataService from '../DataService.js';
import DocService from '../DocService.js';
import { withCostTracking } from '../cost/withCostTracking.js';
import { korchPublish } from '../korch/index.js';
import governedAegisCall from '../aegis/governedAegisCall.js';
import { createLogger } from '../obs/logger.js';

const log = createLogger('ktradecraft');

const MODULES_COLLECTION = 'tradecraft_modules';
const BODY_CHAR_LIMIT = 4000;

const CROCKFORD = '0123456789ABCDEFGHJKMNPQRSTVWXYZ';
function generateUlid() {
  let t = Date.now();
  const tc = new Array(10);
  for (let i = 9; i >= 0; i--) { tc[i] = CROCKFORD[t % 32]; t = Math.floor(t / 32); }
  const rc = new Array(16);
  for (let i = 0; i < 16; i++) rc[i] = CROCKFORD[Math.floor(Math.random() * 32)];
  return tc.join('') + rc.join('');
}

/**
 * List active tradecraft modules for a given vertical.
 * Tenant-read path: only returns active=true modules.
 */
export async function listActiveModules({ vertical }) {
  return DataService.queryDocuments(MODULES_COLLECTION, [
    ['vertical', '==', vertical],
    ['active', '==', true],
  ]).catch(() => []);
}

/**
 * Inject tradecraft context into an AEGIS prompt for a given tenant.
 * Appends relevant active modules (≤6000 chars added) based on tenant's vertical tags.
 * Returns the augmented prompt string.
 */
export async function injectTradecraftContext({ tenantId, verticalTags, aegisPrompt }) {
  if (!verticalTags || verticalTags.length === 0) return aegisPrompt;

  const modulesByVertical = await Promise.all(
    verticalTags.map((v) => listActiveModules({ vertical: v }))
  );
  const modules = modulesByVertical.flat();

  if (modules.length === 0) return aegisPrompt;

  let contextBlock = '\n\n---\n## Vertical Tradecraft Context\n';
  let charsUsed = 0;
  const CONTEXT_CHAR_LIMIT = 6000;

  for (const mod of modules) {
    const entry = `\n### ${mod.topic} (${mod.vertical})\n${mod.body}\n`;
    if (charsUsed + entry.length > CONTEXT_CHAR_LIMIT) break;
    contextBlock += entry;
    charsUsed += entry.length;
  }

  await DocService.write({
    tenantId,
    entryVariant: 'system_event',
    actorId: 'ktradecraft-service',
    actorType: 'system',
    eventTopic: 'ktradecraft.context.injected',
    payload: { tenantId, verticalTags, moduleCount: modules.length, charsInjected: charsUsed },
  });

  await korchPublish({ // types-boundary-allow
    type: 'ktradecraft.context.injected',
    payload: { tenantId, moduleCount: modules.length },
    correlationId: generateUlid(),
    tenantId,
  });

  return aegisPrompt + contextBlock;
}

/**
 * Synthesize a new tradecraft module from source material via AEGIS.
 * Result is flagged synthesized=true, active=false until operator-reviewed.
 */
export async function synthesizeNewModule({ vertical, topic, sourceMaterial, correlationId }) {
  const id = generateUlid();
  const corrId = correlationId ?? id;

  const systemPrompt = `You are a logistics tradecraft expert. Synthesize a concise operational module (≤${BODY_CHAR_LIMIT} chars) for the vertical: ${vertical}. Topic: ${topic}. Include: key operational patterns, regulatory nuances if applicable, and exception-resolution playbook. Cite up to 5 facts. Output JSON: { body, factsCited: string[], confidence: 0.0-1.0 }`;

  const result = await withCostTracking(
    { provider: 'anthropic', purpose: 'ktradecraft-synthesis', tenantId: 'platform', correlationId: corrId },
    () => governedAegisCall({ systemPrompt, userMessage: sourceMaterial, correlationId: corrId })
  );

  let parsed = {};
  try { parsed = JSON.parse(result?.content ?? result?.text ?? '{}'); } catch { /* non-JSON fallback */ }

  const now = new Date().toISOString();
  const module = {
    id,
    vertical,
    topic,
    body: (parsed.body ?? result?.content ?? '').slice(0, BODY_CHAR_LIMIT),
    factsCited: Array.isArray(parsed.factsCited) ? parsed.factsCited : [],
    authoredBy: 'llm-synthesized',
    synthesizedConfidence: typeof parsed.confidence === 'number' ? parsed.confidence : null,
    reviewedByOperatorAt: null,
    active: false,
    versionedAt: now,
    correlationId: corrId,
  };

  await DataService.setDoc(MODULES_COLLECTION, id, module);

  await korchPublish({
    type: 'ktradecraft.module.synthesized',
    payload: { id, vertical, topic, confidence: module.synthesizedConfidence },
    correlationId: corrId,
    tenantId: 'platform',
  });

  await DocService.write({
    tenantId: 'platform',
    entryVariant: 'ai_decision',
    actorId: 'ktradecraft-service',
    actorType: 'system',
    eventTopic: 'ktradecraft.module.synthesized',
    payload: { moduleId: id, vertical, topic, confidence: module.synthesizedConfidence },
    correlationId: corrId,
    confidence: module.synthesizedConfidence,
  });

  log.info({ id, vertical, topic, confidence: module.synthesizedConfidence }, 'ktradecraft: module synthesized (pending review)');
  return module;
}

/**
 * Operator approval: transitions a synthesized module to active=true.
 * Appends a new version record (append-versioned discipline).
 */
export async function approveModule({ moduleId, reviewerUserId, correlationId }) {
  const existing = await DataService.getDoc(MODULES_COLLECTION, moduleId);
  if (!existing) throw new Error(`ktradecraft.approveModule: module ${moduleId} not found`);
  if (existing.active) throw new Error('ktradecraft.approveModule: module is already active');

  const now = new Date().toISOString();
  const updatedId = generateUlid();
  const approved = {
    ...existing,
    id: updatedId,
    active: true,
    reviewedByOperatorAt: now,
    versionedAt: now,
    correlationId: correlationId ?? updatedId,
    supersedes: moduleId,
  };

  await DataService.setDoc(MODULES_COLLECTION, updatedId, approved);

  await korchPublish({
    type: 'ktradecraft.module.approved',
    payload: { id: updatedId, supersedes: moduleId, vertical: existing.vertical, topic: existing.topic, reviewerUserId },
    correlationId: approved.correlationId,
    tenantId: 'platform',
  });

  await DocService.write({
    tenantId: 'platform',
    entryVariant: 'human_override',
    actorId: reviewerUserId,
    actorType: 'user',
    eventTopic: 'ktradecraft.module.approved',
    payload: { moduleId: updatedId, supersedes: moduleId, vertical: existing.vertical, topic: existing.topic },
    correlationId: approved.correlationId,
  });

  log.info({ moduleId: updatedId, supersedes: moduleId, reviewerUserId }, 'ktradecraft: module approved');
  return approved;
}
